/**
 * Подсказки первого входа (§10, M5).
 *
 * Три шага на роль, не больше: столько человек прочитает, прежде чем начнёт
 * тыкать сам. Показываем не «тур по приложению», а ответы на три вопроса,
 * которые пара задаёт в первый день — где начинается смена, как отметить
 * событие, где смотреть деньги.
 *
 * Шаг ждёт, пока его якорь появится в DOM: карточка смены рисуется после
 * ответа сервера, и подсказка, показанная над скелетоном, указывала бы в
 * пустоту. Если якоря на экране нет вовсе — тур останавливается и продолжится
 * в следующий заход, а не показывает подсказку о том, чего человек не видит.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import {
  COACH_KEYS_BY_ROLE,
  useMarkHints,
  useSeenHints,
  type CoachKey,
} from '@/shared/api/hints'
import type { Role } from '@/shared/api/types'
import { CoachMark } from '@/shared/ui/CoachMark'
import { RELEASE_KEYS } from '@/features/whatsnew/releases'

interface Step {
  key: CoachKey
  title: string
  text: string
}

const STEPS: Record<Role, Step[]> = {
  parent: [
    {
      key: 'parent_day',
      title: 'День ребёнка',
      text: 'Здесь появляются события, фото и расходы — по мере того как проходит день. Спрашивать «ну как там?» не нужно.',
    },
    {
      key: 'parent_pay',
      title: 'Часы и оплата',
      text: 'Часы считаются сами из смен, расходы подтягиваются туда же. В конце периода останется проверить и перевести.',
    },
    {
      key: 'parent_family',
      title: 'Настройки семьи',
      text: 'Ставка, расходы на подтверждение, геометки и приглашение няни — всё в одном разделе.',
    },
  ],
  nanny: [
    {
      key: 'nanny_shift',
      title: 'Смена',
      text: 'Начало и конец — одна кнопка. Родитель увидит время сразу, писать об этом отдельно не нужно.',
    },
    {
      key: 'nanny_tiles',
      title: 'Отметить за секунду',
      text: 'Поел, поспал, погуляли — один тап. Можно добавить фото или пару слов, но и без них родитель уже в курсе.',
    },
    {
      key: 'nanny_earnings',
      title: 'Ваши часы',
      text: 'Здесь всегда видно, сколько наработано и сколько выходит. Считает приложение — договариваться на словах не придётся.',
    },
  ],
}

// Как часто ищем якорь очередного шага. Реже — подсказка заметно опаздывает
// после загрузки данных, чаще — бессмысленно: человек не успевает прочитать.
const LOOKUP_INTERVAL_MS = 400

// Сколько ждём именно первый шаг, прежде чем разрешить показать следующий.
//
// Якоря появляются не одновременно: панель вкладок нарисована сразу, а
// карточка смены — только после ответа сервера. Без этой паузы тур начинался
// бы с третьего шага просто потому, что вкладка отрисовалась первой. По
// истечении паузы порядок перестаёт быть обязательным: значит, нужного места
// на экране нет вовсе, и лучше показать следующее, чем не показать ничего.
const PATIENCE_MS = 4000

function anchorExists(key: string): boolean {
  return document.querySelector(`[data-coach="${key}"]`) !== null
}

export function CoachTour({ role }: { role: Role }) {
  const seen = useSeenHints()
  const mark = useMarkHints()
  const [current, setCurrent] = useState<Step | null>(null)
  const [stopped, setStopped] = useState(false)

  const steps = STEPS[role]

  // Показанные в этом заходе. Без них ход тура зависел бы от того, успел ли
  // ответить сервер: отметка «видел» уезжает запросом, и на быстрых нажатиях
  // список ожидающих отставал на шаг — тур возвращался к уже прочитанному.
  // Сервер здесь отвечает за память между заходами, а не за порядок внутри.
  const shownRef = useRef(new Set<string>())

  // Список ожидающих держим в ref: иначе интервал поиска якоря
  // перезапускался бы на каждое обновление данных экрана.
  const pendingRef = useRef<Step[]>([])
  pendingRef.current = steps.filter(
    (step) => !seen.has(step.key) && !shownRef.current.has(step.key),
  )

  useEffect(() => {
    if (stopped || current) return

    const startedAt = Date.now()
    const look = () => {
      const pending = pendingRef.current
      if (pending.length === 0) return
      // Пока ждём — только первый шаг. Дальше — любой, чей якорь на экране.
      const candidates = Date.now() - startedAt < PATIENCE_MS ? pending.slice(0, 1) : pending
      const next = candidates.find((step) => anchorExists(step.key))
      if (next) setCurrent(next)
    }
    look()
    const id = setInterval(look, LOOKUP_INTERVAL_MS)
    return () => clearInterval(id)
  }, [stopped, current])

  /**
   * Закрыть тур целиком. Заодно помечаем прочитанными все выпуски: человек,
   * который только что впервые открыл приложение, не должен получить экран
   * «Что нового» о версиях, которых он не застал.
   */
  const finish = useCallback(() => {
    setCurrent(null)
    setStopped(true)
    mark.mutate([...COACH_KEYS_BY_ROLE[role], ...RELEASE_KEYS])
  }, [mark, role])

  const next = useCallback(() => {
    if (!current) return
    shownRef.current.add(current.key)
    const rest = pendingRef.current.filter((step) => step.key !== current.key)
    const following = rest[0]
    if (!following) {
      finish()
      return
    }
    mark.mutate([current.key])
    // Строго следующий шаг, и только если он уже на экране. Иначе обнуляем:
    // поиск запустится заново и снова подождёт именно его, а не перескочит
    // на тот, чей якорь просто отрисовался раньше.
    setCurrent(anchorExists(following.key) ? following : null)
  }, [current, mark, finish])

  if (!current) return null

  const index = steps.findIndex((step) => step.key === current.key)

  return (
    <CoachMark
      anchor={current.key}
      title={current.title}
      text={current.text}
      step={index + 1}
      total={steps.length}
      onNext={next}
      onSkip={finish}
    />
  )
}
